"use client";

/**
 * One row in the order-summary block on TradePanel — label + value,
 * optional bold/tone, optional hover/focus tooltip via a CSS
 * popover (replaces the native `title=` attribute which had a ~1.5s
 * hover delay; founder bug 2026-05-02). Used for You-pay / You-
 * receive / Max-payout / Potential-profit rows.
 *
 * Extracted from TradePanel.tsx 2026-05-02 — pure presentational, no
 * behaviour change.
 */
export function SummaryRow({
  label,
  value,
  bold,
  tone,
  tooltip,
}: {
  label: string;
  value: string;
  bold?: boolean;
  tone?: "positive" | "negative";
  // STORY-819 (founder ask 2026-05-01) — fee tooltip pattern. When
  // set, the label gets an info icon and a hover/focus popover.
  // Keyboard-accessible via tab (the span is focusable when this
  // prop is set).
  tooltip?: string;
}) {
  const color =
    tone === "positive"
      ? "var(--color-yes)"
      : tone === "negative"
        ? "var(--color-no)"
        : undefined;
  return (
    <div className="flex items-center justify-between py-0.5">
      {tooltip ? (
        <span className="group relative flex items-center gap-1 cursor-help text-[var(--color-text3)]">
          <span
            tabIndex={0}
            aria-label={`${label} — ${tooltip}`}
            className="flex items-center gap-1 outline-none"
          >
            {label}
            <span aria-hidden="true" className="text-[10px] opacity-70">
              ⓘ
            </span>
          </span>
          <span
            role="tooltip"
            className="pointer-events-none absolute left-0 top-full z-20 mt-1 hidden w-max max-w-[260px] rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1.5 text-[10px] font-normal leading-tight text-[var(--color-text2)] shadow-lg group-hover:block group-focus-within:block"
          >
            {tooltip}
          </span>
        </span>
      ) : (
        <span className="text-[var(--color-text3)]">{label}</span>
      )}
      <span
        className={`font-mono tabular-nums ${bold ? "font-bold" : "font-semibold"}`}
        style={color ? { color } : undefined}
      >
        {value}
      </span>
    </div>
  );
}
